import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CreateConceptDialog } from "@/components/concepts/CreateConceptDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePageView } from "@/hooks/usePageView";
import { C } from "@/components/welcome/StageSection";

export const Route = createFileRoute("/welcome")({
  component: WelcomePage,
});

function WelcomePage() {
  usePageView("/welcome");
  const [showCreate, setShowCreate] = useState(false);
  const [entered, setEntered] = useState(false);
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const atConceptLimit =
    currentUser?.plan_limits.max_concepts != null &&
    currentUser.concept_count >= currentUser.plan_limits.max_concepts;

  useEffect(() => {
    requestAnimationFrame(() => setEntered(true));
  }, []);

  function handleSkip() {
    const skipKey = currentUser?.auth_id
      ? `paperchase-welcome-skipped-${currentUser.auth_id}`
      : "paperchase-welcome-skipped";
    localStorage.setItem(skipKey, "1");
    navigate({ to: "/dashboard" });
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#24273a", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px" }}>
        {/* Hero */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 40,
            opacity: entered ? 1 : 0,
            transform: entered ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.7s ease",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              borderRadius: 20,
              backgroundColor: C.mantle,
              border: `1px solid ${C.surface0}`,
              marginBottom: 16,
            }}
          >
            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: C.green }} />
            <span style={{ fontSize: 11, fontWeight: 500, color: C.subtext }}>Welcome to PaperChase</span>
          </div>

          <h1 style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.25, margin: "0 0 14px", maxWidth: 540, marginLeft: "auto", marginRight: "auto" }}>
            Congratulations.
            <br />
            The gap between reading papers and{" "}
            <span
              style={{
                background: `linear-gradient(135deg, ${C.green}, ${C.teal})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              writing your argument
            </span>{" "}
            just closed.
          </h1>

          <p style={{ fontSize: 15, lineHeight: 1.6, color: C.subtext, maxWidth: 480, margin: "0 auto" }}>
            PaperChase is the research platform that takes you from seed papers to a structured,
            evidence-backed argument — with AI handling the work in between.
          </p>
        </div>

        {/* CTA */}
        <div
          style={{
            textAlign: "center",
            marginTop: 0,
            opacity: entered ? 1 : 0,
            transition: "opacity 0.8s ease 0.2s",
          }}
        >
          <Button
            size="lg"
            onClick={() => (currentUser?.features?.setup_v2 ? navigate({ to: "/concepts/new" }) : setShowCreate(true))}
            disabled={atConceptLimit}
            title={atConceptLimit ? `You've reached the plan limit of ${currentUser?.plan_limits.max_concepts} concepts` : undefined}
            style={{
              padding: "14px 36px",
              fontSize: 15,
              fontWeight: 600,
              backgroundColor: C.green,
              color: "#24273a",
              border: "none",
              borderRadius: 8,
            }}
          >
            <Plus className="mr-1.5 size-4" />
            Create your first concept
          </Button>
          <button
            className="mt-4 block w-full text-[13px] transition-colors hover:text-foreground"
            style={{ color: C.overlay0 }}
            onClick={handleSkip}
          >
            Skip to dashboard
          </button>
        </div>
      </div>

      <CreateConceptDialog open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
